import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Building2, MapPin, Phone, Mail, Globe, Save, X, ArrowLeft, UserPlus, User, Lock } from 'lucide-react';
import api from '../../services/api';
import { t as tr } from '../../i18n'

export default function RestaurantForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    currency: 'OMR',
    taxRate: 0,
    serviceCharge: 0,
    timezone: 'Asia/Muscat',
    language: 'en',
    isActive: true,
    business_type: 'restaurant',
    createAdmin: false,
    adminUsername: '',
    adminPassword: '',
    adminFullName: '',
    adminEmail: '',
    adminPhone: ''
  });

  useEffect(() => {
    if (isEditMode) {
      loadRestaurant();
    }
  }, [id]);

  const loadRestaurant = async () => {
    try {
      const response = await api.get(`/api/restaurants/${id}`);
      const restaurant = response.data.restaurant;
      const settings = typeof restaurant.settings === 'string' 
        ? JSON.parse(restaurant.settings) 
        : restaurant.settings || {};

      setFormData({
        business_type: restaurant.business_type || 'restaurant',
        name: restaurant.name || '',
        code: restaurant.code || '',
        address: restaurant.address || '',
        phone: restaurant.phone || '',
        email: restaurant.email || '',
        website: restaurant.website || '',
        description: restaurant.description || '',
        currency: settings.currency || 'OMR',
        taxRate: settings.tax_rate ?? 0,
        serviceCharge: settings.service_charge ?? 0,
        timezone: settings.timezone || 'Asia/Muscat',
        language: settings.language || 'en',
        isActive: restaurant.is_active
      });
    } catch (error) {
      console.error('Failed to load restaurant:', error);
      alert(tr("Failed to load restaurant details"));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        code: formData.code,
        address: formData.address,
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
        description: formData.description,
        business_type: formData.business_type,
        settings: {
          currency: formData.currency,
          tax_rate: parseFloat(formData.taxRate) || 0,
          service_charge: parseFloat(formData.serviceCharge) || 0,
          timezone: formData.timezone,
          language: formData.language
        },
        isActive: formData.isActive
      };

      // Add admin employee data if creating new restaurant and option is selected
      if (!isEditMode && formData.createAdmin) {
        if (!formData.adminUsername || !formData.adminPassword || !formData.adminFullName) {
          alert(tr("Please fill in all required admin employee fields"));
          setLoading(false);
          return;
        }
        payload.createAdmin = true;
        payload.adminEmployee = {
          username: formData.adminUsername,
          password: formData.adminPassword,
          full_name: formData.adminFullName,
          email: formData.adminEmail,
          phone: formData.adminPhone
        };
      }

      if (isEditMode) {
        await api.put(`/api/restaurants/${id}`, payload);
        alert(tr("Restaurant updated successfully!"));
      } else {
        await api.post('/api/restaurants', payload);
        alert(tr("Restaurant created successfully!"));
      }
      
      navigate('/owner');
    } catch (error) {
      console.error('Failed to save restaurant:', error);
      alert(tr(error.response?.data?.error || 'Failed to save restaurant'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/owner')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            {tr("Back to Dashboard")}
          </button>
          <h1 className="text-3xl font-bold text-slate-900">
            {isEditMode ? tr("Edit Restaurant") : tr("Create New Restaurant")}
          </h1>
          <p className="text-slate-600 mt-2">
            {isEditMode ? tr("Update restaurant information and settings") : tr("Add a new restaurant to your portfolio")}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          {/* Business Type (only chosen at creation - drives which admin sections and storefront this store gets) */}
          {!isEditMode && (
            <div>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">{tr("What kind of business is this?")}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { value: 'restaurant', label: 'Restaurant' },
                  { value: 'ecommerce', label: 'Online Store' },
                  { value: 'appointments', label: 'Appointments' },
                  { value: 'hotel', label: 'Hotel' },
                  { value: 'office', label: 'Office / Coworking' },
                ].map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, business_type: t.value })}
                    className={`px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${formData.business_type === t.value ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  >
                    {tr(t.label)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              {tr("Basic Information")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {tr("Restaurant Name *")}
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder={tr("e.g., Muscat Downtown")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {tr("Branch Code *")}
                </label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder={tr("e.g., DWN")}
                  maxLength={10}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <MapPin className="w-4 h-4 inline me-1" />
                  {tr("Address *")}
                </label>
                <textarea
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  rows="3"
                  placeholder={tr("Full street address")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Phone className="w-4 h-4 inline me-1" />
                  {tr("Phone Number")}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder={tr("+968 XXXX XXXX")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Mail className="w-4 h-4 inline me-1" />
                  {tr("Email Address")}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder={tr("contact@restaurant.com")}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Globe className="w-4 h-4 inline me-1" />
                  {tr("Website (Optional)")}
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder={tr("https://www.restaurant.com")}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {tr("Description")}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  rows="3"
                  placeholder={tr("Brief description of the restaurant")}
                />
              </div>
            </div>
          </div>

          {/* Admin Employee Creation (only for new restaurants) */}
          {!isEditMode && (
            <div className="border-t pt-8">
              <div className="mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.createAdmin}
                    onChange={(e) => setFormData({ ...formData, createAdmin: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-blue-600" />
                    {tr("Create Admin Employee for this Restaurant")}
                  </span>
                </label>
                <p className="text-sm text-slate-600 mt-2 ms-8">
                  {tr("Create the first admin user who will manage this restaurant")}
                </p>
              </div>

              {formData.createAdmin && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 p-6 bg-blue-50 rounded-xl">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <User className="w-4 h-4 inline me-1" />
                      {tr("Username *")}
                    </label>
                    <input
                      type="text"
                      required={formData.createAdmin}
                      value={formData.adminUsername}
                      onChange={(e) => setFormData({ ...formData, adminUsername: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder={tr("admin_username")}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <Lock className="w-4 h-4 inline me-1" />
                      {tr("Password *")}
                    </label>
                    <input
                      type="password"
                      required={formData.createAdmin}
                      value={formData.adminPassword}
                      onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder={tr("Secure password")}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {tr("Full Name *")}
                    </label>
                    <input
                      type="text"
                      required={formData.createAdmin}
                      value={formData.adminFullName}
                      onChange={(e) => setFormData({ ...formData, adminFullName: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder={tr("John Doe")}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <Mail className="w-4 h-4 inline me-1" />
                      {tr("Email")}
                    </label>
                    <input
                      type="email"
                      value={formData.adminEmail}
                      onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder={tr("admin@example.com")}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <Phone className="w-4 h-4 inline me-1" />
                      {tr("Phone")}
                    </label>
                    <input
                      type="tel"
                      value={formData.adminPhone}
                      onChange={(e) => setFormData({ ...formData, adminPhone: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder={tr("+968 XXXX XXXX")}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Settings */}
          <div className="border-t pt-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">{tr("Restaurant Settings")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {tr("Currency")}
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  {['OMR','SAR','AED','QAR','KWD','BHD','MAD','USD','EUR','GBP','CAD','AUD','CHF','JPY','CNY','TRY','EGP','SEK','NOK','DKK','INR'].map(code => <option key={code} value={code}>{code}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {tr("Tax Rate (%)")}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.taxRate}
                  onChange={(e) => setFormData({ ...formData, taxRate: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {tr("Service Charge (%)")}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.serviceCharge}
                  onChange={(e) => setFormData({ ...formData, serviceCharge: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {tr("Timezone")}
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="Africa/Casablanca">{tr("Africa/Casablanca")}</option>
                  <option value="Europe/London">{tr("Europe/London")}</option>
                  <option value="America/New_York">{tr("America/New_York")}</option>
                  <option value="Asia/Dubai">{tr("Asia/Dubai")}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {tr("Language")}
                </label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="en">{tr("English")}</option>
                  <option value="fr">{tr("Français")}</option>
                  <option value="ar">العربية</option>
                  <option value="es">{tr("Español")}</option>
                </select>
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    {tr("Restaurant is Active")}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/owner')}
              className="px-6 py-3 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium transition-all flex items-center gap-2"
            >
              <X className="w-5 h-5" />
              {tr("Cancel")}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 font-medium transition-all flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-blue-500/30"
            >
              <Save className="w-5 h-5" />
              {loading ? tr("Saving...") : isEditMode ? tr("Update Restaurant") : tr("Create Restaurant")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
